import { Key, Globe, Network, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DocCodeBlock } from '../../../components/docs/DocCodeBlock';
import { DocsPageHeader } from '../../../components/docs/DocsPageHeader';

export function QuickstartContent() {
  const { t } = useTranslation('docs');
  return (
    <div className="space-y-16">
      <DocsPageHeader
        badge={t('quickstart.badge')}
        title={t('quickstart.heading')}
        description={t('quickstart.description')}
      />

      {/* Step 1: Obtain API Key */}
      <section className="space-y-5">
        <div className="flex items-center gap-2 text-primary">
          <Key className="h-5 w-5" />
          <h3 className="text-2xl font-semibold text-foreground">{t('quickstart.step1_heading')}</h3>
        </div>
        <p className="text-sm text-muted-foreground">{t('quickstart.step1_desc')}</p>
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
          <ol className="list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
            <li>{t('quickstart.step1_item1')}</li>
            <li>{t('quickstart.step1_item2')}</li>
            <li>{t('quickstart.step1_item3')}</li>
            <li>{t('quickstart.step1_item4')}</li>
          </ol>
          <div className="mt-4">
            <Link
              to="/console/api"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            >
              {t('quickstart.step1_link')}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Step 2: Set Base URL */}
      <section className="space-y-5">
        <div className="flex items-center gap-2 text-primary">
          <Globe className="h-5 w-5" />
          <h3 className="text-2xl font-semibold text-foreground">{t('quickstart.step2_heading')}</h3>
        </div>
        <p className="text-sm text-muted-foreground">{t('quickstart.step2_desc')}</p>
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{t('quickstart.step2_url_label')}</p>
            <DocCodeBlock>{`https://api.etherworks.cn/v1`}</DocCodeBlock>
          </div>
          <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            <li>{t('quickstart.step2_item1')}</li>
            <li>{t('quickstart.step2_item2')}</li>
          </ul>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{t('quickstart.step2_env_label')}</p>
            <DocCodeBlock>{`export APP_API_KEY=ether_xxxxxxxxxxxxxxxxxxxxxxxx`}</DocCodeBlock>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{t('quickstart.step2_sdk_label')}</p>
            <DocCodeBlock>{`from openai import OpenAI

client = OpenAI(
    api_key="YOUR_API_KEY",
    base_url="https://api.etherworks.cn/v1"
)`}</DocCodeBlock>
          </div>
        </div>
      </section>

      {/* Step 3: Get Model List */}
      <section className="space-y-5">
        <div className="flex items-center gap-2 text-primary">
          <Network className="h-5 w-5" />
          <h3 className="text-2xl font-semibold text-foreground">{t('quickstart.step3_heading')}</h3>
        </div>
        <p className="text-sm text-muted-foreground">{t('quickstart.step3_desc')}</p>
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm space-y-4">
          <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            <li>{t('quickstart.step3_item1')}</li>
            <li>{t('quickstart.step3_item2')}</li>
          </ul>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{t('quickstart.step3_curl_label')}</p>
            <DocCodeBlock>{`curl https://api.etherworks.cn/v1/models \\
  -H "Authorization: Bearer $APP_API_KEY"`}</DocCodeBlock>
          </div>
        </div>
      </section>

      {/* Next Steps */}
      <section className="space-y-6">
        <h3 className="text-2xl font-semibold text-foreground">{t('quickstart.next_steps_heading')}</h3>
        <p className="text-sm text-muted-foreground">{t('quickstart.next_steps_desc')}</p>
        <div className="grid gap-4 md:grid-cols-3">
          <Link to="/docs/overview/examples" className="rounded-3xl border border-border bg-card p-6 shadow-sm hover:border-primary/30 transition group">
            <h4 className="text-base font-semibold text-foreground group-hover:text-primary transition">{t('quickstart.next_steps_examples')}</h4>
            <p className="mt-2 text-sm text-muted-foreground">{t('quickstart.next_steps_examples_desc')}</p>
          </Link>
          <Link to="/docs/api/chat-completions" className="rounded-3xl border border-border bg-card p-6 shadow-sm hover:border-primary/30 transition group">
            <h4 className="text-base font-semibold text-foreground group-hover:text-primary transition">{t('quickstart.next_steps_api')}</h4>
            <p className="mt-2 text-sm text-muted-foreground">{t('quickstart.next_steps_api_desc')}</p>
          </Link>
          <Link to="/docs/api/models" className="rounded-3xl border border-border bg-card p-6 shadow-sm hover:border-primary/30 transition group">
            <h4 className="text-base font-semibold text-foreground group-hover:text-primary transition">{t('quickstart.next_steps_models')}</h4>
            <p className="mt-2 text-sm text-muted-foreground">{t('quickstart.next_steps_models_desc')}</p>
          </Link>
        </div>
      </section>
    </div>
  );
}

export function FAQContent() {
  const { t } = useTranslation('docs');

  return (
    <div className="space-y-12">
      <section className="max-w-3xl">
        <DocsPageHeader
          badge={t('faq.badge')}
          title={t('faq.heading')}
          description={t('faq.description')}
        />
      </section>

      <section className="space-y-10">
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-foreground">{t('faq.what_models')}</h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t('faq.what_models_answer')}
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-foreground">{t('faq.keys_expire')}</h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t('faq.keys_expire_answer')}
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-foreground">{t('faq.choose_model')}</h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t('faq.choose_model_answer')}
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-foreground">{t('faq.rate_limits')}</h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t('faq.rate_limits_answer')}
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-foreground">{t('faq.data_private')}</h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t('faq.data_private_answer')}
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-foreground">{t('faq.supports_streaming')}</h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t('faq.supports_streaming_answer')}
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-foreground">{t('faq.get_help')}</h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t('faq.get_help_answer')}
          </p>
        </div>
      </section>
    </div>
  );
}

export function ModelsContent() {
  const { t } = useTranslation('docs');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-primary">
        <Network className="h-5 w-5" />
        <h3 className="text-2xl font-semibold text-foreground">{t('quickstart.available_models')}</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        {t('quickstart.available_models_desc')}
      </p>
      <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
        <div>
          <h4 className="text-sm font-semibold text-foreground uppercase tracking-[0.2em]">{t('quickstart.requirements')}</h4>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            <li>{t('quickstart.requirements_item')}</li>
          </ul>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        {t('quickstart.requirements_tip')}
      </p>
    </div>
  );
}
